﻿import { useState } from "react";
import { Alert, Box, Button, Link, Paper, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import {
  DashedDivider,
  RegistrationJourneyButtons,
  RegistrationStepPage,
} from "components";
import { useRegistrationJourney } from "journey";
import { extractApiError, formatDate, getAge } from "utils";
import { GTag, useTermsAndConditions } from "hooks";
import { api, ContentType, CreateCustomerRequest } from "api";
import { PersonalDetails } from "./sections/PersonalDetails";
import { AddressDetails } from "./sections/AddressDetails";
import { ContactDetails } from "./sections/ContactDetails";
import { SpecialRequirements } from "./sections/SpecialRequirements";
import { PermissionToLeave } from "./sections/PermissionToLeave";
import { usePaymentDeadline } from "./usePaymentDeadline";

export const Summary = () => {
  const { t } = useTranslation();
  const {
    candidateDetails,
    selectedExam,
    marketingConsent,
    termsAccepts,
    confirmRegistration,
  } = useRegistrationJourney();
  const [registrationId, setRegistrationId] = useState<string>();

  const { fileName } = useTermsAndConditions(selectedExam?.id);
  const { isPending, mutate, error } = useMutation({
    mutationFn: async ({
      request,
      specialArrangementsFiles,
    }: {
      request: CreateCustomerRequest;
      specialArrangementsFiles?: File[];
    }) => {
      let _registrationId = registrationId;
      if (!_registrationId) {
        const result = await api.customers.createCustomer(request);
        setRegistrationId(result.data);
        _registrationId = result.data;
      }

      if (specialArrangementsFiles) {
        await api.customers.uploadCustomerDocuments(
          _registrationId,
          {
            files: specialArrangementsFiles,
          },
          { type: ContentType.FormData }
        );
      }
      return _registrationId;
    },
    onSuccess(guid) {
      GTag.purchase(selectedExam, guid);
      confirmRegistration(guid);
    },
  });

  const paymentDeadline = usePaymentDeadline(selectedExam);
  const age = getAge(selectedExam?.examDate, candidateDetails?.dateOfBirth);
  const showPermissionToLeave = age && age < 18;
  const showSpecialRequirements =
    candidateDetails?.specialRequirements?.disability ||
    candidateDetails?.specialRequirements?.dyslexia;

  const handleApplyNow = () => {
    if (!candidateDetails || !selectedExam) {
      throw new Error("Candidate details and exam are required");
    }

    GTag.setUserData(candidateDetails);

    const createCustomerRequest: CreateCustomerRequest = {
      firstName: candidateDetails.firstName,
      middleName: candidateDetails.middleName,
      lastName: candidateDetails.lastName,
      gender: candidateDetails.gender,
      birthDate: candidateDetails.dateOfBirth,
      email: candidateDetails.emailAddress,
      phoneNumber: candidateDetails.phoneNumber,
      guardian: candidateDetails.guardian,
      address: {
        street: candidateDetails.address.street,
        city: candidateDetails.address.city,
        postalCode: candidateDetails.address.postalCode,
      },
      permissionToLeave: showPermissionToLeave
        ? {
            canLeave: candidateDetails.permissionToLeave?.canLeave || false,
            guardianName:
              candidateDetails.permissionToLeave?.guardianName || "",
            guardianPhone:
              candidateDetails.permissionToLeave?.guardianPhone || "",
            personCollectingCandidate:
              candidateDetails.permissionToLeave?.personCollectingCandidate ||
              "",
          }
        : null,
      specialRequirements: showSpecialRequirements
        ? {
            disability:
              candidateDetails.specialRequirements?.disability || false,
            dyslexia: candidateDetails.specialRequirements?.dyslexia || false,
            description:
              candidateDetails.specialRequirements?.description || "",
          }
        : null,
      termsConsent: !!termsAccepts?.termsAccepted,
      photoConsent: !!termsAccepts?.termsPhotoAccepted,
      marketingConsent: !!marketingConsent,
      examDateId: selectedExam.id,
    };

    mutate({
      request: createCustomerRequest,
      specialArrangementsFiles: candidateDetails.specialRequirements?.files,
    });
  };

  if (!candidateDetails) {
    return null;
  }

  return (
    <RegistrationStepPage stepIndex={3}>
      <Typography variant="h3" sx={{ mb: 8 }}>
        {t("summary.title")}
      </Typography>
      <Paper component={Box} variant="outlined" sx={{ p: 2, mb: 8 }}>
        <Typography sx={{ mb: 2 }}>
          <Trans
            i18nKey="summary.paragraph1"
            values={{ date: formatDate(paymentDeadline) }}
            components={{ bold: <strong /> }}
          />
        </Typography>
        <Typography sx={{ mb: 2 }}>
          <Trans
            i18nKey={"summary.paragraph2"}
            components={{
              CustomLink: (
                <Link
                  href={`https://bccdn.britishcouncil.org/apps/entrynet/regulations/${fileName}`}
                  target="_blank"
                  sx={{ display: "inline!important" }}
                />
              ),
            }}
          />
        </Typography>
        <Typography sx={{ mb: 3 }}>{t("summary.paragraph3")}</Typography>
      </Paper>

      <PersonalDetails candidateDetails={candidateDetails} />
      <AddressDetails candidateDetails={candidateDetails} />
      <ContactDetails candidateDetails={candidateDetails} />

      {showSpecialRequirements && (
        <SpecialRequirements candidateDetails={candidateDetails} />
      )}

      {showPermissionToLeave && (
        <PermissionToLeave candidateDetails={candidateDetails} />
      )}

      <DashedDivider sx={{ mb: 4 }} />
      {error && (
        <Alert severity="error">
          {extractApiError(error, t("common.generalError"))}
        </Alert>
      )}
      <RegistrationJourneyButtons>
        <Button
          variant="contained"
          sx={{ minWidth: "206px", ml: { sm: "auto" } }}
          onClick={handleApplyNow}
          loading={isPending}
        >
          {t("summary.applyNow")}
        </Button>
      </RegistrationJourneyButtons>
    </RegistrationStepPage>
  );
};
